import { Alert, Badge, Button, Card, Form, ListGroup, Modal } from 'react-bootstrap';
import { AppShell } from '../../components/AppShell/AppShell';
import { Footer } from '../../components/Footer/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartPlus, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { useStripe } from '@stripe/react-stripe-js';
import { Browser } from '@capacitor/browser';
import { useCapacitor } from '../../../hooks/useCapacitor';
import { Link, Redirect } from 'react-router-dom';
import { formatSecondsTimestamp } from '../../utils/utils';
import { Trans, useTranslation } from 'react-i18next';
import styles from './Gift.module.scss';
import { Title } from '../../components/Title/Title';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { HighlightedCard } from '../../components/HighlightedCard/HighlightedCard';

const GiftComponent = () => {
  const { getStripeOneOffs, createOneOffSession, userDataFmt, staticData } = useFirebase();
  const { t } = useTranslation();
  const { currentLanguage } = useCurrentLanguage();

  const { isNative } = useCapacitor();
  const [loading, setLoading] = useState(false);
  const [oneOffs, setOneOffs] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [cart, setCart] = useState<any[]>([]);
  const [billingInfoModal, setBillingInfoModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'buy' | 'history'>('buy');
  const cartMap = useRef(new Map());

  const stripe = useStripe();

  useEffect(() => {
    setLoading(true);
    getStripeOneOffs()
      .then(({ products, history }) => {
        setOneOffs(products);
        setHistory(history);
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const proceedToCheckout = () => {
    setBillingInfoModal(false);
    setLoading(true);
    createOneOffSession({ customer: userDataFmt.stripeId, cart })
      .then(async ({ sessionId, url }) => {
        if (isNative) {
          await Browser.open({ url });
        } else await stripe.redirectToCheckout({ sessionId });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const onCartSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBillingInfoModal(true);
  };

  const getLocalizedProperty = (item: any, prop: string) => {
    try {
      return item.metadata?.[`i18n.${prop}.${currentLanguage}`] || item[prop];
    } catch {
      return item[prop];
    }
  };

  if (loading || !userDataFmt || !stripe || !staticData || !Object.keys(staticData).length) return <FullscreenLoader />;

  if (!staticData.ENABLE_GIFTS) return <Redirect to={'/'} />;

  return (
    <AppShell>
      <div className={styles.page}>
      <Title>{t('gift.labels.regalaAbbonamento')}</Title>

      <div className={styles.content}>
        <h1 className="text-page-title text-center">{t('gift.labels.regalaAbbonamento')}</h1>
        {!!oneOffs.length && <p className="text-page-subtitle text-center mb-4">{t('gift.labels.scontoRegalo')}</p>}
        <section className="tabbed-workspace" aria-label={t('gift.labels.regalaAbbonamento')}>
          <div className="tabbed-workspace-tabs" role="tablist" aria-label={t('gift.labels.regalaAbbonamento')}>
            <button
              type="button"
              role="tab"
              id="gift-tab-buy"
              aria-selected={activeTab === 'buy'}
              aria-controls="gift-panel-buy"
              className={activeTab === 'buy' ? 'tabbed-workspace-active' : undefined}
              onClick={() => setActiveTab('buy')}
            >
              {t('gift.labels.acquista')}
            </button>
            <button
              type="button"
              role="tab"
              id="gift-tab-history"
              aria-selected={activeTab === 'history'}
              aria-controls="gift-panel-history"
              className={activeTab === 'history' ? 'tabbed-workspace-active' : undefined}
              onClick={() => setActiveTab('history')}
            >
              {t('labels.storico')}
            </button>
          </div>

          <div className="tabbed-workspace-panel">
          {activeTab === 'buy' && (
            <div id="gift-panel-buy" role="tabpanel" aria-labelledby="gift-tab-buy">
              <header className="mb-5">
                <p>{t('gift.labels.giftDescrizione')}</p>
                <p className="mt-3">{t('gift.labels.giftDescrizione2')}</p>
              </header>
        {!oneOffs.length && (
          <Alert variant={'warning'} className={'mt-4 text-center'}>
            {t('gift.labels.noPacchetti')}
          </Alert>
        )}

        {!!oneOffs.length && (
          <>
            <div className={'d-flex flex-column flex-md-row mt-4'}>
              {oneOffs.map((oneOff, index, arr) => (
                <HighlightedCard key={oneOff.id} className={'flex-fill w-100 ' + (index == arr.length - 1 ? 'mr-0 mb-0' : 'mr-0 mr-md-3 mb-3 mb-md-0')}>
                  <Card.Body>
                    <Card.Title>{getLocalizedProperty(oneOff, 'name')}</Card.Title>
                    <div>{getLocalizedProperty(oneOff, 'description')}</div>
                    <div className={'d-flex align-items-center justify-content-between mt-3'}>
                      <div>
                        {oneOff.prices[0].metadata.FULL && <span className={`mr-2 ${styles.deletedPrice}`}>&euro; {oneOff.prices[0].metadata.FULL}</span>}
                        <span className={'h4 mb-0'}>
                          <Badge variant={'success'}>€ {(oneOff.prices[0].unit_amount / 100).toFixed(2)}</Badge>
                        </span>
                      </div>
                      <Button
                        className={'tootor-button tootor-button--secondary'}
                        size={'sm'}
                        onClick={() => {
                          if (!cartMap.current.get(oneOff.id)) cartMap.current.set(oneOff.id, 0);
                          cartMap.current.set(oneOff.id, cartMap.current.get(oneOff.id) + 1);
                          setCart((current) => [...current, { itemId: `${oneOff.id}-${cartMap.current.get(oneOff.id)}`, ...oneOff }]);
                        }}
                      >
                        <FontAwesomeIcon icon={faCartPlus} className={'mr-1'} /> {t('labels.aggiungi')}
                      </Button>
                    </div>
                  </Card.Body>
                </HighlightedCard>
              ))}
            </div>
            <div className={'small text-muted mt-3'}>
              {t('gift.labels.disclamer1')}
              <br />
              {t('gift.labels.disclamer2')}
              <br />
              {t('gift.labels.disclamer3')}
            </div>
          </>
        )}

        {!!oneOffs.length && (
          <>
            <div className={'d-flex align-items-center mt-4'}>
              <div className={'h3'}>
                {t('labels.carrello')}
              </div>
            </div>

            {!cart.length && (
              <>
                <div className={'text-center'} style={{ fontSize: '1.2rem' }}>
                  {t('gift.labels.carrelloVuoto')}
                </div>
                <div className={'text-center text-muted'}>{t('gift.labels.aggiungiSconti')}</div>
              </>
            )}

            {!!cart.length && (
              <Form onSubmit={onCartSubmit}>
                <div>{t('gift.labels.compilaEmail')}</div>
                <div>{t('gift.labels.compilaEmailDescrizione')}</div>
                <div className={'mt-2'}>
                  {cart.map((item) => (
                    <HighlightedCard key={item.itemId} className={'mb-2'}>
                      <div className={'d-flex flex-column flex-md-row align-items-start align-items-md-center'}>
                        <div className={'d-flex flex-column flex-grow-1'}>
                          <div>
                            <h5 className={'mb-0'}>{getLocalizedProperty(item, 'name')}</h5>
                          </div>
                          <div>€ {(item.prices[0].unit_amount / 100).toFixed(2)}</div>
                        </div>
                        <div className={'form-page d-flex'}>
                          <Form.Group className={'mb-0 mr-2'}>
                            <Form.Control
                              placeholder={t('gift.labels.nomeDestinatario')}
                              required
                              value={item.recipientName}
                              onChange={(e) => (item.recipientName = e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group className={'mb-0'}>
                            <Form.Control
                              placeholder={t('gift.labels.emailDestinatario')}
                              type={'email'}
                              required
                              value={item.recipient}
                              onChange={(e) => (item.recipient = e.target.value)}
                            />
                          </Form.Group>
                        </div>
                        <div className={'ml-2'}>
                          <Button
                            className={'tootor-button tootor-button--primary'}
                            onClick={() => {
                              setCart((current) => current.filter((i) => i.itemId != item.itemId));
                              cartMap.current.set(item.id, cartMap.current.get(item.id) - 1);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </div>
                      </div>
                    </HighlightedCard>
                  ))}
                </div>
                <HighlightedCard className={'mb-2'}>
                    <div className={'d-flex justify-content-between'}>
                      <h5 className={'mb-0'}>Totale:</h5>
                      <strong>€ {(cart.map((item) => item.prices[0].unit_amount).reduce((acc, val) => acc + val) / 100).toFixed(2)}</strong>
                    </div>
                </HighlightedCard>
                <div className={'d-flex justify-content-end mt-2'}>
                  <Button className={'tootor-button tootor-button--primary'} type={'submit'}>
                    {t('gift.labels.procediPagamento')}
                  </Button>
                </div>
              </Form>
            )}
          </>
        )}
            </div>
          )}

          {activeTab === 'history' && (
            <div id="gift-panel-history" role="tabpanel" aria-labelledby="gift-tab-history">
        {!history.length && (
          <>
            <div className={'text-center'} style={{ fontSize: '1.2rem' }}>
              {t('gift.labels.nessunAcquisto')}
            </div>
            <div className={'text-center text-muted'}>{t('gift.labels.nessunAcquistoDescrizione')}</div>
          </>
        )}

        {!!history.length && (
          <div>
            {history.map((item) => (
              <div className={'listCard mb-2'} key={item.id}>
                <div className={'d-flex justify-content-between align-items-center'}>
                  <div className={'flex-fill'}>
                    {t('gift.labels.codici', { date: formatSecondsTimestamp(item.ts._seconds, 'dd/MM/y HH:mm'), length: item.coupons.length })}
                  </div>
                  <div>
                    <Button className={'tootor-button tootor-button--basic'} size={'sm'} as={Link} to={`/gift/details/${item.id}`}>
                      {t('labels.dettagli')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
            </div>
          )}
          </div>
        </section>
      </div>

      <Footer />
      </div>

      <Modal className="tootor-modal" show={billingInfoModal} onHide={() => setBillingInfoModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('labels.confirmInfoModalSubtitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('labels.confirmInfoModalDescription1')}</p>
          <p className="mb-0">{t('labels.confirmInfoModalDescription2')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button className="tootor-button tootor-button--basic" onClick={() => setBillingInfoModal(false)}>
            {t('labels.annulla')}
          </Button>
          <Button className="tootor-button tootor-button--secondary" onClick={proceedToCheckout}>
            {t('labels.continua')}
          </Button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
};

export default GiftComponent;
